/**
 * EL ARBOL COMO MAPA (ARBOL-GRAFO-VISTA-T1, Josse 3-nov, Ares #42417).
 *
 * Lee la forma del contrato (#42427) y calcula DONDE va cada cosa en el mapa: el proyecto en el centro, sus grupos en un
 * anillo y los nodos de cada grupo como hojas alrededor de su grupo. El estado, los usos y el grupo vienen de la base: el
 * panel no los calcula, solo los dibuja. Sin DOM: lo prueba scripts/check-arbol-mapa.mjs.
 *
 * @module application/arbol/mapaDelArbol
 */

const de = (o, ...ks) => { for (const k of ks) if (o && o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const arr = (v) => (Array.isArray(v) ? v : []);

export const ESTADOS = ['vive', 'abierto', 'falta', 'no_usar'];

// quien_lo_usa llega de la base como texto con forma de lista ('["equipo Nexo en ..."]', medido en arbol_grafo el
// 3-nov): se lee como lista y se dice separada por comas, sin corchetes ni comillas en la ficha.
function comoLista(v) {
  if (Array.isArray(v)) return v.map(String).join(', ');
  const s = String(v ?? '').trim();
  if (s.startsWith('[')) {
    try { const l = JSON.parse(s); if (Array.isArray(l)) return l.map(String).join(', '); } catch { /* texto suelto: va tal cual */ }
  }
  return s;
}

/** La respuesta del contrato, en una forma (camel y snake). null si no trae nodos. */
export function leerArbol(data) {
  const d = data || {};
  const p = de(d, 'proyecto', 'Proyecto') || {};
  const nodos = arr(de(d, 'nodos', 'Nodos')).map((n) => {
    const estado = String(de(n, 'estado', 'Estado') ?? '').toLowerCase();
    return {
      id: String(de(n, 'id', 'Id') ?? de(n, 'item_ref', 'itemRef') ?? ''),
      ref: String(de(n, 'item_ref', 'itemRef', 'ItemRef') ?? de(n, 'id', 'Id') ?? ''),
      tipo: String(de(n, 'tipo', 'Tipo') ?? ''),
      grupo: String(de(n, 'grupo', 'Grupo') ?? 'otros'),
      estado: ESTADOS.includes(estado) ? estado : 'falta',
      usos: Math.max(0, Number(de(n, 'usos', 'Usos')) || 0),
      version: String(de(n, 'version', 'Version') ?? ''),
      queHace: String(de(n, 'que_hace', 'queHace', 'QueHace') ?? ''),
      quienLoUsa: comoLista(de(n, 'quien_lo_usa', 'quienLoUsa', 'QuienLoUsa')),
      prueba: String(de(n, 'prueba', 'Prueba') ?? '')
    };
  }).filter((n) => n.id);
  if (!nodos.length) return null;
  const declarados = arr(de(d, 'grupos', 'Grupos')).map((g) => ({ id: String(de(g, 'id', 'Id') ?? ''), nombre: String(de(g, 'nombre', 'Nombre') ?? de(g, 'id', 'Id') ?? '') })).filter((g) => g.id);
  // Un grupo que traen los nodos y no esta declarado se agrega con su id como nombre (no se pierde ningun nodo).
  const grupos = [...declarados];
  for (const n of nodos) if (!grupos.some((g) => g.id === n.grupo)) grupos.push({ id: n.grupo, nombre: n.grupo });
  const ids = new Set(nodos.map((n) => n.id));
  const aristas = arr(de(d, 'aristas', 'Aristas'))
    .map((a) => ({ de: String(de(a, 'de', 'De', 'from') ?? ''), a: String(de(a, 'a', 'A', 'to') ?? ''), tipo: String(de(a, 'tipo', 'Tipo') ?? '') }))
    .filter((a) => ids.has(a.de) && ids.has(a.a) && a.de !== a.a);
  return { proyecto: { key: String(de(p, 'key', 'Key') ?? ''), nombre: String(de(p, 'nombre', 'Nombre') ?? de(p, 'key', 'Key') ?? '') }, grupos, nodos, aristas };
}

/** Radio del circulo de un nodo: crece con cuantos lo usan, con tope para que nadie tape a sus vecinos. */
export const radioDelNodo = (usos) => Math.round(6 + Math.min(10, Math.sqrt(usos) * 2));

/**
 * Las posiciones: grupos repartidos en un anillo de radio R1 (en el orden en que llegan) y los nodos de cada grupo en
 * anillos concentricos alrededor de su grupo, con espacio suficiente para su radio. Determinista: el mismo arbol da el
 * mismo dibujo (el usuario no pierde de vista donde estaba cada cosa al recargar).
 * @returns {{ centro:{x,y}, grupos: {id,nombre,x,y,angulo}[], nodos: Record<string,{x,y,r}>, extension:number }}
 */
export function disposicionRadial(arbol, { R1: minimo = 220, paso = 34, holgura = 40 } = {}) {
  const G = arbol.grupos.length;
  // El radio de cada racimo (su ultimo anillo de hojas mas la hoja mas grande): el anillo de grupos se abre lo necesario
  // para que ningun racimo tape el centro ni a su vecino (dos racimos vecinos separados por la cuerda del anillo).
  const racimo = arbol.grupos.map((g) => {
    const hijos = arbol.nodos.filter((n) => n.grupo === g.id);
    let anillos = 0;
    for (let quedan = hijos.length; quedan > 0; quedan -= 6 * ++anillos);
    return (anillos ? paso * anillos + 20 : 0) + Math.max(14, ...hijos.map((n) => radioDelNodo(n.usos)));
  });
  let R1 = Math.max(minimo, ...racimo.map((r) => r + holgura + 22));
  if (G > 1) {
    const cuerda = 2 * Math.sin(Math.PI / G);
    for (let i = 0; i < G; i++) R1 = Math.max(R1, (racimo[i] + racimo[(i + 1) % G] + holgura) / cuerda);
  }
  R1 = Math.ceil(R1);
  const grupos = arbol.grupos.map((g, i) => {
    const angulo = (2 * Math.PI * i) / G - Math.PI / 2;
    return { ...g, angulo, x: Math.round(R1 * Math.cos(angulo)), y: Math.round(R1 * Math.sin(angulo)) };
  });
  const nodos = {};
  let extension = R1;
  for (const g of grupos) {
    const hijos = arbol.nodos.filter((n) => n.grupo === g.id);
    // Hojas en anillos alrededor del grupo: 6 en el primero, 12 en el segundo, 18 en el tercero...
    let i = 0, anillo = 1;
    while (i < hijos.length) {
      const cupo = 6 * anillo;
      const radio = paso * anillo + 20;
      for (let k = 0; k < cupo && i < hijos.length; k++, i++) {
        const a = g.angulo + (2 * Math.PI * k) / cupo;
        const n = hijos[i];
        nodos[n.id] = { x: Math.round(g.x + radio * Math.cos(a)), y: Math.round(g.y + radio * Math.sin(a)), r: radioDelNodo(n.usos) };
        extension = Math.max(extension, Math.hypot(nodos[n.id].x, nodos[n.id].y) + nodos[n.id].r);
      }
      anillo++;
    }
  }
  return { centro: { x: 0, y: 0 }, grupos, nodos, extension: Math.ceil(extension + 40) };
}

/** Cuantos nodos hay por estado (la leyenda dice cifras, no solo colores). */
export function conteoPorEstado(arbol) {
  const c = Object.fromEntries(ESTADOS.map((e) => [e, 0]));
  for (const n of arbol.nodos) c[n.estado]++;
  return c;
}

/** La causa en palabras, por motivo del cliente (regla F3). */
export function fraseDeLaFalla(motivo) {
  if (motivo === 'NO_PUBLICADO') return 'La API servida no tiene la ruta del arbol (404). Desde la 1.6.1009 la tiene: revisa a que API apunta el panel.';
  if (motivo === 'PROHIBIDO') return 'Tu sesion no tiene permiso para ver el arbol.';
  if (motivo === 'SIN_RED') return 'No pudimos hablar con la API ahora. Intenta de nuevo.';
  return 'La API respondio con un error.';
}
